/**
 * AgentCore Runtime invoke — Cognito Identity Pool + aws4fetch で SigV4 署名
 */
import { AwsClient } from "aws4fetch";
import agentcoreConfig from "../agentcoreConfig";

const REGION = agentcoreConfig.region;
const IDENTITY_POOL_ID = agentcoreConfig.identityPoolId;
const USER_POOL_ID = agentcoreConfig.userPoolId;
const RUNTIME_ARN = agentcoreConfig.runtimeArn;

const ENDPOINT = `https://bedrock-agentcore.${REGION}.amazonaws.com`;
const INVOKE_PATH = `/runtimes/${encodeURIComponent(RUNTIME_ARN)}/invocations`;

interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
}

let cachedCredentials: AwsCredentials | null = null;
let credentialsExpiry = 0;

async function getCredentials(idToken: string): Promise<AwsCredentials> {
  if (cachedCredentials && Date.now() < credentialsExpiry) return cachedCredentials;

  const cognitoIdentityUrl = `https://cognito-identity.${REGION}.amazonaws.com`;
  const logins = { [`cognito-idp.${REGION}.amazonaws.com/${USER_POOL_ID}`]: idToken };

  const getIdRes = await fetch(cognitoIdentityUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": "AWSCognitoIdentityService.GetId" },
    body: JSON.stringify({ IdentityPoolId: IDENTITY_POOL_ID, Logins: logins }),
  });
  if (!getIdRes.ok) throw new Error(`Identity取得失敗: ${getIdRes.status}`);
  const { IdentityId } = await getIdRes.json();

  const getCredsRes = await fetch(cognitoIdentityUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-amz-json-1.1", "X-Amz-Target": "AWSCognitoIdentityService.GetCredentialsForIdentity" },
    body: JSON.stringify({ IdentityId, Logins: logins }),
  });
  if (!getCredsRes.ok) throw new Error(`クレデンシャル取得失敗: ${getCredsRes.status}`);
  const { Credentials } = await getCredsRes.json();

  cachedCredentials = {
    accessKeyId: Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken: Credentials.SessionToken,
  };
  credentialsExpiry = Credentials.Expiration * 1000 - 60_000;
  return cachedCredentials;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export async function invokeAgentChat(
  prompt: string,
  idToken: string,
  model?: string,
): Promise<string> {
  const creds = await getCredentials(idToken);

  const aws = new AwsClient({
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    sessionToken: creds.sessionToken,
    region: REGION,
    service: "bedrock-agentcore",
  });

  const body = JSON.stringify({ prompt, token: `Bearer ${idToken}`, ...(model && { model }) });

  const res = await aws.fetch(`${ENDPOINT}${INVOKE_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  });

  if (!res.ok) {
    const err = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(`AgentCore呼び出し失敗: ${err}`);
  }

  const raw = await res.text();
  const lines = raw.split("\n");
  let result = "";
  for (const line of lines) {
    if (line.startsWith("data: ")) {
      try { result += JSON.parse(line.slice(6)); } catch { result += line.slice(6); }
    }
  }
  return result || raw;
}
